import { onLibraryWrite } from './library';
import { createIndexer, type Indexer } from './searchIndexer';
import type { IndexHit } from './searchIndex';

/**
 * Keeps the search index fed from the library, and answers queries.
 *
 * The index lives in a Web Worker when the browser has them (see
 * searchIndexer.ts for why), and in the page otherwise. Callers cannot tell
 * which: both are asynchronous and give the same results.
 *
 * Writes update a single document, which is the whole point of indexing:
 * `O(document)` on write instead of `O(library)` per keystroke.
 */

export type SearchRequest =
  | { type: 'seed' }
  | { type: 'refresh'; id: string }
  | { type: 'reset' }
  | { type: 'search'; requestId: number; query: string; limit?: number };

export type SearchResponse =
  { type: 'ready' } | { type: 'hits'; requestId: number; hits: IndexHit[] };

export interface Backend {
  seed(): Promise<void>;
  refresh(id: string): void;
  search(query: string, limit?: number): Promise<IndexHit[]>;
  reset(): void;
  isReady(): boolean;
}

export function localBackend(indexer: Indexer = createIndexer()): Backend {
  let ready = false;
  return {
    seed: () => indexer.seed().then(() => void (ready = true)),
    refresh: (id) => void indexer.refresh(id),
    search: (query, limit) => indexer.search(query, limit),
    reset: () => {
      ready = false;
      indexer.reset();
    },
    isReady: () => ready,
  };
}

/** Exported for tests, which drive it with a fake worker. */
export function workerBackend(worker: Worker, fallback: () => Backend): Backend {
  let ready = false;
  let seeded: Promise<void> | null = null;
  let resolveSeed: (() => void) | null = null;
  let nextId = 0;
  const pending = new Map<number, (hits: IndexHit[]) => void>();
  // Set if the worker dies (a failed load, say); from then on the page does the work.
  let replacement: Backend | null = null;

  worker.onmessage = (event: MessageEvent<SearchResponse>) => {
    const message = event.data;
    if (message.type === 'ready') {
      ready = true;
      resolveSeed?.();
    } else {
      pending.get(message.requestId)?.(message.hits);
      pending.delete(message.requestId);
    }
  };
  worker.onerror = () => {
    replacement = fallback();
    worker.terminate();
    void replacement.seed().then(() => resolveSeed?.());
    for (const [, resolve] of pending) resolve([]);
    pending.clear();
  };

  const send = (message: SearchRequest) => worker.postMessage(message);

  return {
    seed() {
      if (replacement) return replacement.seed();
      seeded ??= new Promise<void>((resolve) => {
        resolveSeed = resolve;
        send({ type: 'seed' });
      });
      return seeded;
    },
    refresh(id) {
      if (replacement) replacement.refresh(id);
      else send({ type: 'refresh', id });
    },
    search(query, limit) {
      if (replacement) return replacement.search(query, limit);
      const requestId = ++nextId;
      return new Promise((resolve) => {
        pending.set(requestId, resolve);
        send({ type: 'search', requestId, query, limit });
      });
    },
    reset() {
      ready = false;
      seeded = null;
      if (replacement) replacement.reset();
      else send({ type: 'reset' });
    },
    isReady: () => replacement?.isReady() ?? ready,
  };
}

function createBackend(): Backend {
  if (typeof Worker === 'undefined') return localBackend();
  try {
    const worker = new Worker(new URL('./search.worker.ts', import.meta.url), { type: 'module' });
    return workerBackend(worker, () => localBackend());
  } catch {
    return localBackend();
  }
}

let backend: Backend | null = null;
let unsubscribe: (() => void) | null = null;

function current(): Backend {
  backend ??= createBackend();
  return backend;
}

/**
 * Start (or join) the index build and keep it current. Idempotent, so callers
 * can simply await it before searching.
 */
export function startSearchIndex(): Promise<void> {
  unsubscribe ??= onLibraryWrite((id) => current().refresh(id));
  return current().seed();
}

/** True once the whole library has been indexed. */
export function searchIndexReady(): boolean {
  return backend?.isReady() ?? false;
}

/** Tear the index down (tests, and sign-out, where the library is replaced). */
export function resetSearchIndex(): void {
  unsubscribe?.();
  unsubscribe = null;
  backend?.reset();
}

export type { IndexHit };

/** Run a query against the index, waiting for the initial build to finish. */
export async function searchLibrary(query: string, limit?: number): Promise<IndexHit[]> {
  await startSearchIndex();
  return current().search(query, limit);
}
